/* Watercraft design tokens.
 *
 * Mirrored from the Watercraft Flutter app (`watercraft_mobile`), which is the
 * source of truth for the live product. The app does not centralise its dark
 * palette — every screen redeclares the same private constants — so these
 * values are the set those declarations agree on. `calendar_screen.dart`
 * carries the fullest list and is the closest thing to a canonical reference.
 *
 * Where the handoff pack's marketing HTML used a slightly different shade for
 * the same role, the app's value wins so the web surfaces and the app read as
 * one product.
 */

export const wcColors = {
  // Grounds and surfaces
  bg: '#0C1A0F',
  surface: '#162419',
  surfaceRaised: '#1E3024',
  border: '#2A4030',
  borderSoft: '#16221A',

  // Text
  textPrimary: '#EEF4EB',

  /* Body copy. The app's `sync_banner.dart` declares exactly this as its own
     `_textSecondary` — a lighter, less saturated tier than the `#8AAB8A` the
     card screens use for labels. Paragraphs read as washed out in the label
     colour, so body copy takes this one. */
  textBody: '#9AB49B',

  // Card labels and eyebrows.
  textSecondary: '#8AAB8A',

  /* One step below the labels. The app has no single token here because it
     splits the tier in two — in the moon card it sets the MOONRISE label in
     textMuted and the time itself in textPrimary. The compact cards run
     label and value together on one line ("RISE 10:11 · SET 22:41"), which
     needs the shade between the two. */
  textTertiary: '#6E8270',

  textMuted: '#4D7052',

  /* Long-form reading colour: a softer, greener cream than textPrimary. The
     app sets article body copy in it (`article_screen.dart`) and italic
     quoted catch notes (`catch_view_sheet.dart`), which is the same
     secondary-emphasis role the marketing headline's italic line plays. */
  textReading: '#D6E2D2',

  /* The app's emphasis cream — "today", active markers, accent icons. Warm
     and tan, distinctly not a green; not interchangeable with textReading. */
  warm: '#E6D9BD',

  // Brand greens
  primary: '#1A5C2A',
  primaryLight: '#2E8C47',
  accent: '#3DFF6A',

  // Status
  warning: '#F5A623',
  error: '#EF4444',
  info: '#3B9EBF',

  /* Deep water, for the stylised lake glyph in the "My waters" card. No app
     equivalent: the app draws a water's thumbnail as a real satellite image
     (`static_map_thumb.dart`), so the glyph exists only on the marketing
     surfaces and keeps the pack's navy. */
  water: '#0B2D4A',
};

/* Row dividers. The app's hairline is textPrimary at ~6% over the surface
   (`Color(0x0FEEF4EB)`), not a solid shade — a solid one close enough to the
   surface to look right disappears against it entirely. */
export const wcHairline = 'rgba(238,244,235,.06)';

/* The accent is near-white in luminance, so anything sitting on an accent
   fill takes the ground colour rather than a text colour. */
export const wcOnAccent = wcColors.bg;

/* Hover for the solid neon CTA. The app has no hover state — it is touch
   only — so this is a web-only lift of the accent. */
export const wcAccentHover = '#5CFF83';

/* Libre Franklin for body and UI, JetBrains Mono for data, labels and times.
   Those are the only two families the app uses (666 and 243 call sites).
   Playfair Display is a marketing face only and never appears in the app —
   the pack's in-app cards deliberately set headlines in the bold sans. */
export const wcFonts = {
  body: "'Libre Franklin',Helvetica,Arial,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,SFMono-Regular,monospace",
  display: "'Playfair Display',Georgia,serif",
};

/* Radii the app settles on: 12 for cards, 999 for pills. The 20 comes from
   `defaultBorderRadius` in `ui_constant.dart` and suits the large glass
   panels on the marketing surfaces. */
export const wcRadius = {
  card: '12px',
  panel: '20px',
  pill: '999px',
};

/* The app tints by laying a token colour over the ground at low alpha —
   backgrounds around 0.05–0.18, borders around 0.35–0.5 — rather than by
   using separate solid shades. Mirrors `Color.withOpacity` in Dart. */
const RGB = {
  [wcColors.accent]: '61,255,106',
  [wcColors.primary]: '26,92,42',
  [wcColors.primaryLight]: '46,140,71',
  [wcColors.warning]: '245,166,35',
  [wcColors.error]: '239,68,68',
  [wcColors.info]: '59,158,191',
  [wcColors.textPrimary]: '238,244,235',
  [wcColors.warm]: '230,217,189',
};

export function wcTint(color, alpha) {
  const rgb = RGB[color];
  if (!rgb) throw new Error(`wcTint: no rgb registered for ${color}`);
  return `rgba(${rgb},${alpha})`;
}

/* The same tint flattened against a background into an opaque colour.
   Needed wherever a tinted shape straddles two different backgrounds — a
   translucent fill would show the seam through it. */
export function wcSolidTint(color, alpha, base = wcColors.bg) {
  const fg = RGB[color];
  if (!fg) throw new Error(`wcSolidTint: no rgb registered for ${color}`);
  const f = fg.split(',').map(Number);
  const b = [1, 3, 5].map((i) => parseInt(base.slice(i, i + 2), 16));
  const hex = f
    .map((v, i) => Math.round(alpha * v + (1 - alpha) * b[i]))
    .map((v) => v.toString(16).padStart(2, '0'))
    .join('');
  return `#${hex.toUpperCase()}`;
}
